import { ApiError, tryRefreshSession } from './http';
import { getAccessToken } from './tokens';

const BASE = import.meta.env.VITE_MAPS_API_BASE_URL ?? 'https://x-gis.xenus.dev/api/maps';

/** Аналитические таблицы — нешейповые типизированные записи (core.object_types/
 * objects на бэкенде), для сущностей вроде показаний электроэнергии, у которых
 * сама запись не имеет собственного положения на карте. См. серверный модуль
 * app/routers/analytical_tables.py для полного объяснения, почему это не слой
 * и не справочник. */
export type AnalyticalFieldDataType = 'string' | 'text' | 'integer' | 'decimal' | 'boolean' | 'date' | 'datetime' | 'dictionary';

export type AnalyticalTableField = {
  code: string;
  name: string;
  data_type: AnalyticalFieldDataType;
  dictionary_id: string | null;
  /** Когда задано — это строковое поле хранит id объекта слоя с этим code;
   * фронтенд должен рисовать пикер по этому слою, а не текстовый инпут. */
  reference_layer_code: string | null;
  is_required: boolean;
};

export type AnalyticalTable = {
  id: string;
  code: string;
  name: string;
  row_count: number;
};

export type AnalyticalTableDetail = AnalyticalTable & { fields: AnalyticalTableField[] };

const REFERENCE_MIRROR_SUFFIX = ' (объект на карте)';

/** A reference field like meter_feature_id (name "Прибор учёта (объект на
 * карте)") has a plain-text sibling like meter_ref (name "Прибор учёта") that
 * exists only to hold a readable label — the same name, minus this suffix. When
 * found, the form should fill that sibling from the picked reference's title
 * instead of asking the user to retype what they just selected. */
export function findReferenceMirrorField(
  fields: AnalyticalTableField[],
  referenceField: AnalyticalTableField,
): AnalyticalTableField | undefined {
  if (!referenceField.name.endsWith(REFERENCE_MIRROR_SUFFIX)) return undefined;
  const baseName = referenceField.name.slice(0, -REFERENCE_MIRROR_SUFFIX.length);
  return fields.find((f) => f.code !== referenceField.code && f.name === baseName);
}

export type AnalyticalRow = {
  id: string;
  name: string;
  attributes: Record<string, unknown>;
};

async function authFetch(path: string, options: RequestInit = {}, _retried = false): Promise<Response> {
  const headers = new Headers(options.headers);
  const token = getAccessToken();
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${BASE}${path}`, { ...options, headers });

  if (response.status === 401 && !_retried && (await tryRefreshSession())) {
    return authFetch(path, options, true);
  }
  if (!response.ok) {
    let message = 'Не удалось выполнить запрос';
    try {
      const body = await response.json();
      if (typeof body.detail === 'string') message = body.detail;
    } catch {
      // тело ответа не JSON — оставляем сообщение по умолчанию
    }
    throw new ApiError(response.status, message);
  }
  return response;
}

export const analyticalTablesApi = {
  listTables: async (): Promise<AnalyticalTable[]> => {
    const response = await authFetch('/analytical-tables');
    return response.json();
  },

  getTable: async (tableId: string): Promise<AnalyticalTableDetail> => {
    const response = await authFetch(`/analytical-tables/${tableId}`);
    return response.json();
  },

  listRows: async (tableId: string): Promise<AnalyticalRow[]> => {
    const response = await authFetch(`/analytical-tables/${tableId}/rows`);
    return response.json();
  },

  createRow: async (tableId: string, attributes: Record<string, unknown>): Promise<AnalyticalRow> => {
    const response = await authFetch(`/analytical-tables/${tableId}/rows`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    return response.json();
  },

  updateRow: async (tableId: string, rowId: string, attributes: Record<string, unknown>): Promise<AnalyticalRow> => {
    const response = await authFetch(`/analytical-tables/${tableId}/rows/${rowId}`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes }),
    });
    return response.json();
  },

  deleteRow: async (tableId: string, rowId: string): Promise<void> => {
    await authFetch(`/analytical-tables/${tableId}/rows/${rowId}`, { method: 'DELETE' });
  },

  getEnergyReport: async (tableId: string, kind: 'plan_fact' | 'monthly' | 'consumption') => {
    const response = await authFetch(`/analytical-tables/${tableId}/reports/energy?kind=${kind}`);
    return response.json() as Promise<{ columns: string[]; rows: (string | number | null)[][] }>;
  },

  exportEnergyReportExcel: async (tableId: string, kind: 'plan_fact' | 'monthly' | 'consumption'): Promise<Blob> => {
    const response = await authFetch(`/analytical-tables/${tableId}/reports/energy?kind=${kind}&format=excel`);
    return response.blob();
  },

  /** Ведомость по электроэнергии с разнесением затрат по бюджету — см.
   * app/routers/analytical_tables.py's analytical_budget_report/
   * _resolve_meter_budget for how "какой бюджет" gets decided. */
  getBudgetReport: async (
    tableId: string,
  ): Promise<{ columns: string[]; rows: (string | number | null)[][]; totals: { budget: string; fact_kwh: number; cost: number }[] }> => {
    const response = await authFetch(`/analytical-tables/${tableId}/reports/budget`);
    return response.json();
  },

  exportBudgetReportExcel: async (tableId: string): Promise<Blob> => {
    const response = await authFetch(`/analytical-tables/${tableId}/reports/budget?format=excel`);
    return response.blob();
  },
};
